import * as THREE from 'three';
import { clone as cloneSkeletonHierarchy } from 'three/addons/utils/SkeletonUtils.js';
import type {
  CharacterAnimationAlias,
  CharacterCustomization,
  CharacterRig,
  CharacterSocketName,
} from './types';
import { findFirstMesh } from './_internals';
import { disposeHead, updateHeadSocketBinding } from './head-swap';
import { disposeEyeSprite, updateEyeMeshes } from './eye-sprite';
import { disposeCape } from './cape';
import {
  logCharacterAnimationDebug,
  type CharacterAnimationActionDebugState,
  type CharacterAnimationDebugState,
} from './animation-debug';
import {
  CHARACTER_ANIMATION_CAPABILITIES,
  getCharacterAnimationCapability,
} from './animation-capabilities';

const SOCKET_BONE_NAMES: Record<CharacterSocketName, readonly string[]> = {
  head: ['Head'],
  face: ['headfront', 'Head'],
  cape: ['neck', 'Spine', 'Spine01', 'Spine02'],
  leftHand: ['LeftHand'],
  rightHand: ['RightHand'],
};

const ANIMATION_ALIASES = buildAnimationAliases();

function buildAnimationAliases(): Record<CharacterAnimationAlias, readonly string[]> {
  const aliases: Partial<Record<CharacterAnimationAlias, readonly string[]>> = { idle: [] };
  for (const capability of CHARACTER_ANIMATION_CAPABILITIES) {
    aliases[capability.alias] = [capability.sourceClip];
  }
  return aliases as Record<CharacterAnimationAlias, readonly string[]>;
}

export function characterAnimationClipMatchesAlias(
  alias: CharacterAnimationAlias,
  clipName: string,
): boolean {
  return clipMatchesAlias(alias, clipName);
}

export function characterAnimationClipsContainAlias(
  clips: readonly THREE.AnimationClip[],
  alias: CharacterAnimationAlias,
): boolean {
  if (alias === 'idle') return true;
  return clips.some((clip) => characterAnimationClipMatchesAlias(alias, clip.name));
}

const ONE_SHOT_ANIMATION_ALIASES = new Set<string>(
  CHARACTER_ANIMATION_CAPABILITIES.filter((capability) => capability.loop === 'once').map(
    (capability) => capability.alias.toLowerCase(),
  ),
);
const CLAMPED_ONE_SHOT_ANIMATION_ALIASES = new Set<string>(
  CHARACTER_ANIMATION_CAPABILITIES.filter((capability) => capability.loop === 'clamp').map(
    (capability) => capability.alias.toLowerCase(),
  ),
);
const DEBUG_ONE_SHOT_ANIMATION_ALIASES = new Set<string>([
  ...ONE_SHOT_ANIMATION_ALIASES,
  ...CLAMPED_ONE_SHOT_ANIMATION_ALIASES,
]);

/**
 * Assembles a CharacterRig from a manifest-loaded body GLB scene.
 *
 * Rigged bodies keep their complete cloned hierarchy and animation clips.
 * Static test/fallback bodies are wrapped in the same root/visual/model shape
 * and get bbox-derived socket groups so callers can use one attachment path.
 */
export function assembleCharacter(args: {
  bodyGltfScene: THREE.Group;
  customization: CharacterCustomization;
  animations?: readonly THREE.AnimationClip[];
}): CharacterRig {
  const { bodyGltfScene, animations = [] } = args;
  const sourceMesh = findFirstMesh(bodyGltfScene);
  if (sourceMesh === null) {
    throw new Error(
      '[assembleCharacter] bodyGltfScene contains no THREE.Mesh children. ' +
        'Ensure the character body GLB is valid and has at least one mesh.',
    );
  }

  const root = new THREE.Group();
  root.name = 'character-root';
  const visualRoot = new THREE.Group();
  visualRoot.name = 'character-visual-root';
  root.add(visualRoot);

  const modelRoot = cloneSkeletonHierarchy(bodyGltfScene) as THREE.Group;
  modelRoot.name = bodyGltfScene.name || 'character-model-root';
  visualRoot.add(modelRoot);

  const renderMeshes: THREE.Mesh[] = [];
  const colorableMeshes: THREE.Mesh[] = [];
  const ownedGeometries = new Set<THREE.BufferGeometry>();
  const ownedMaterials = new Set<THREE.Material>();
  const bonesByName = new Map<string, THREE.Object3D>();
  let bodySkinnedMesh: THREE.SkinnedMesh | null = null;

  modelRoot.traverse((child) => {
    if (child instanceof THREE.Bone) {
      bonesByName.set(child.name, child);
      return;
    }
    if (!(child instanceof THREE.Mesh)) return;

    child.geometry = child.geometry.clone();
    ownedGeometries.add(child.geometry);

    if (child instanceof THREE.SkinnedMesh && bodySkinnedMesh === null) {
      bodySkinnedMesh = child;
    }

    const sourceMaterial = child.material;
    const material =
      bodySkinnedMesh === child ? createBodyMaterial() : cloneMaterial(sourceMaterial);
    child.material = material;
    collectMaterials(material, ownedMaterials);
    child.castShadow = true;
    child.receiveShadow = true;

    renderMeshes.push(child);
    colorableMeshes.push(child);
  });

  const bodyMesh = (bodySkinnedMesh ?? renderMeshes[0]) as THREE.Mesh | undefined;
  if (bodyMesh == null) {
    throw new Error('[assembleCharacter] cloned body scene contains no renderable meshes.');
  }

  const staticSockets = createStaticSockets(bodyMesh, visualRoot);
  const mixer = new THREE.AnimationMixer(modelRoot);
  const actions = new Map<string, THREE.AnimationAction>();
  let activeAction: THREE.AnimationAction | null = null;
  let activeAlias: string | null = null;
  let animationDebugState: CharacterAnimationDebugState = {
    requestedAlias: null,
    resolvedAlias: null,
    resolvedClipName: null,
    success: false,
    activeBefore: null,
    activeAfter: null,
    lastFailureReason: null,
    suspiciousReasons: [],
  };
  mixer.addEventListener('finished', (event) => {
    const finishedAction = (event as { action?: THREE.AnimationAction }).action;
    if (finishedAction !== activeAction) return;
    if (!finishedAction.clampWhenFinished) {
      activeAction = null;
      activeAlias = null;
    }
  });

  registerCharacterAnimationClips({
    mixer,
    modelRoot,
    actions,
    clips: animations,
  });

  function resolveSocket(name: CharacterSocketName | string): THREE.Object3D | null {
    const candidates = SOCKET_BONE_NAMES[name as CharacterSocketName] ?? [name];
    for (const candidate of candidates) {
      const bone = bonesByName.get(candidate);
      if (bone != null) return bone;
    }
    return staticSockets.get(name) ?? null;
  }

  function playAnimation(
    alias: CharacterAnimationAlias | string,
    options: { readonly timeScale?: number } = {},
  ): boolean {
    const requestedAlias = String(alias);
    const activeBefore = snapshotAction(activeAction, activeAlias);
    const action = actions.get(alias) ?? actions.get(alias.toLowerCase());
    if (action == null) {
      if (alias === 'idle') {
        if (activeAction != null) {
          activeAction.fadeOut(0.12);
          activeAction = null;
          activeAlias = null;
        }
        recordAnimationPlay({
          requestedAlias,
          resolvedAlias: 'idle',
          action: null,
          success: true,
          activeBefore,
          failureReason: null,
        });
        return true;
      }
      recordAnimationPlay({
        requestedAlias,
        resolvedAlias: null,
        action: null,
        success: false,
        activeBefore,
        failureReason: 'missing alias',
      });
      return false;
    }
    configureAnimationAction(action, alias, options);
    if (activeAction === action) {
      if (!action.isRunning() && !action.clampWhenFinished) {
        action.reset();
        action.play();
      }
      activeAlias = requestedAlias;
      recordAnimationPlay({
        requestedAlias,
        resolvedAlias: requestedAlias,
        action,
        success: true,
        activeBefore,
        failureReason: null,
      });
      return true;
    }
    action.enabled = true;
    action.reset();
    action.fadeIn(0.12);
    action.play();
    if (activeAction != null) activeAction.fadeOut(0.12);
    activeAction = action;
    activeAlias = requestedAlias;
    recordAnimationPlay({
      requestedAlias,
      resolvedAlias: requestedAlias,
      action,
      success: true,
      activeBefore,
      failureReason: null,
    });
    return true;
  }

  function tick(dt: number): void {
    const safeDt = Number.isFinite(dt) ? Math.min(Math.max(dt, 0), 0.1) : 0;
    mixer?.update(safeDt);
    modelRoot.updateMatrixWorld(true);
    updateHeadSocketBinding(rig);
    updateEyeMeshes(rig, safeDt);
    rig._capeSimulation?.update(safeDt);
    updateFinishedOneShotDebugState();
  }

  function getAnimationDebugState(): CharacterAnimationDebugState {
    updateFinishedOneShotDebugState();
    return animationDebugState;
  }

  function resetSimulation(): void {
    rig._capeSimulation?.reset();
  }

  let disposed = false;
  function dispose(): void {
    if (disposed) return;
    disposed = true;
    disposeHead(rig);
    disposeEyeSprite(rig);
    disposeCape(rig);
    mixer?.stopAllAction();
    for (const geometry of ownedGeometries) geometry.dispose();
    for (const material of ownedMaterials) material.dispose();
    root.removeFromParent();
  }

  const rig: CharacterRig = {
    root,
    visualRoot,
    modelRoot,
    bodyMesh,
    bodySkinnedMesh,
    renderMeshes,
    colorableMeshes,
    mixer,
    actions,
    tick,
    playAnimation,
    getAnimationDebugState,
    resetSimulation,
    resolveSocket,
    dispose,
    _headRoot: null,
    _headMesh: null,
    _eyeSprite: null,
    _eyeGroup: null,
    _eyeGeneration: 0,
    _eyeExpression: 'base',
    _capeMesh: null,
    _capeSimulation: null,
  };

  playAnimation('idle');
  return rig;

  function recordAnimationPlay(args: {
    readonly requestedAlias: string;
    readonly resolvedAlias: string | null;
    readonly action: THREE.AnimationAction | null;
    readonly success: boolean;
    readonly activeBefore: CharacterAnimationActionDebugState | null;
    readonly failureReason: string | null;
  }): void {
    const activeAfter = snapshotAction(activeAction, activeAlias);
    const resolvedClipName = args.action?.getClip().name ?? null;
    const suspiciousReasons = suspiciousReasonsForRigPlay({
      requestedAlias: args.requestedAlias,
      resolvedAlias: args.resolvedAlias,
      resolvedClipName,
      success: args.success,
      activeAfter,
      failureReason: args.failureReason,
    });
    animationDebugState = {
      requestedAlias: args.requestedAlias,
      resolvedAlias: args.resolvedAlias,
      resolvedClipName,
      success: args.success,
      activeBefore: args.activeBefore,
      activeAfter,
      lastFailureReason: args.failureReason,
      suspiciousReasons,
    };
    logCharacterAnimationDebug({
      source: 'rig',
      scope: root.uuid,
      suspiciousReasons,
      payload: {
        requestedAlias: args.requestedAlias,
        resolvedAlias: args.resolvedAlias,
        resolvedClipName,
        success: args.success,
        failureReason: args.failureReason,
        activeBefore: args.activeBefore,
        activeAfter,
      },
    });
  }

  function updateFinishedOneShotDebugState(): void {
    if (activeAction === null) {
      if (animationDebugState.activeAfter !== null) {
        animationDebugState = { ...animationDebugState, activeAfter: null };
      }
      return;
    }
    const activeAfter = snapshotAction(activeAction, activeAlias);
    const activeKey = activeAfter?.alias?.toLowerCase() ?? '';
    if (
      activeAfter === null ||
      !DEBUG_ONE_SHOT_ANIMATION_ALIASES.has(activeKey) ||
      activeAfter.running
    ) {
      return;
    }
    if (!activeAfter.clampWhenFinished) {
      activeAction = null;
      activeAlias = null;
      animationDebugState = { ...animationDebugState, activeAfter: null };
      return;
    }
    const reason = `one-shot appears active after finishing: ${activeAfter.alias}`;
    if (animationDebugState.suspiciousReasons.includes(reason)) return;
    const suspiciousReasons = [...animationDebugState.suspiciousReasons, reason];
    animationDebugState = { ...animationDebugState, activeAfter, suspiciousReasons };
    logCharacterAnimationDebug({
      source: 'rig',
      scope: root.uuid,
      suspiciousReasons,
      payload: {
        requestedAlias: animationDebugState.requestedAlias,
        resolvedAlias: animationDebugState.resolvedAlias,
        resolvedClipName: animationDebugState.resolvedClipName,
        success: animationDebugState.success,
        failureReason: animationDebugState.lastFailureReason,
        activeBefore: animationDebugState.activeBefore,
        activeAfter,
      },
    });
  }
}

export function addCharacterAnimationClips(
  rig: CharacterRig,
  clips: readonly THREE.AnimationClip[],
): void {
  if (clips.length === 0) return;
  if (rig.mixer == null || rig.modelRoot == null || rig.actions == null) return;
  registerCharacterAnimationClips({
    mixer: rig.mixer,
    modelRoot: rig.modelRoot,
    actions: rig.actions,
    clips,
  });
}

function createBodyMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 0.72,
    metalness: 0.02,
  });
}

function cloneMaterial(
  material: THREE.Material | THREE.Material[],
): THREE.Material | THREE.Material[] {
  return Array.isArray(material) ? material.map((mat) => mat.clone()) : material.clone();
}

function collectMaterials(
  material: THREE.Material | THREE.Material[],
  output: Set<THREE.Material>,
): void {
  if (Array.isArray(material)) {
    for (const mat of material) output.add(mat);
    return;
  }
  output.add(material);
}

function resolveActionByName(
  actionsByClipName: ReadonlyMap<string, THREE.AnimationAction>,
  candidates: readonly string[],
): THREE.AnimationAction | null {
  for (const candidate of candidates) {
    const action =
      actionsByClipName.get(candidate) ?? actionsByClipName.get(candidate.toLowerCase());
    if (action != null) return action;
  }
  return null;
}

function registerCharacterAnimationClips(args: {
  readonly mixer: THREE.AnimationMixer;
  readonly modelRoot: THREE.Object3D;
  readonly actions: Map<string, THREE.AnimationAction>;
  readonly clips: readonly THREE.AnimationClip[];
}): void {
  const actionsByClipName = new Map<string, THREE.AnimationAction>();
  for (const [name, action] of args.actions) {
    actionsByClipName.set(name, action);
    actionsByClipName.set(name.toLowerCase(), action);
  }

  for (const clip of args.clips.map(sanitizeLocomotionClip)) {
    if (args.actions.has(clip.name)) continue;
    const action = args.mixer.clipAction(clip, args.modelRoot);
    configureAnimationAction(action, clip.name);
    args.actions.set(clip.name, action);
    args.actions.set(clip.name.toLowerCase(), action);
    actionsByClipName.set(clip.name, action);
    actionsByClipName.set(clip.name.toLowerCase(), action);
  }

  for (const [alias, candidates] of Object.entries(ANIMATION_ALIASES)) {
    const direct = actionsByClipName.get(alias) ?? actionsByClipName.get(alias.toLowerCase());
    const action = direct ?? resolveActionByName(actionsByClipName, candidates);
    if (action != null) {
      configureAnimationAction(action, alias);
      args.actions.set(alias, action);
    }
  }
}

function snapshotAction(
  action: THREE.AnimationAction | null,
  alias: string | null,
): CharacterAnimationActionDebugState | null {
  if (action === null) return null;
  const clip = action.getClip();
  return {
    alias,
    clipName: clip.name,
    time: action.time,
    duration: clip.duration,
    running: action.isRunning(),
    loop: action.loop,
    weight: action.getEffectiveWeight(),
    enabled: action.enabled,
    clampWhenFinished: action.clampWhenFinished,
  };
}

function suspiciousReasonsForRigPlay(args: {
  readonly requestedAlias: string;
  readonly resolvedAlias: string | null;
  readonly resolvedClipName: string | null;
  readonly success: boolean;
  readonly activeAfter: CharacterAnimationActionDebugState | null;
  readonly failureReason: string | null;
}): string[] {
  const reasons: string[] = [];
  if (!args.success) {
    reasons.push(args.failureReason ?? `requested alias failed: ${args.requestedAlias}`);
  }
  if (
    args.success &&
    args.resolvedAlias !== null &&
    args.resolvedClipName !== null &&
    !clipMatchesAlias(args.resolvedAlias, args.resolvedClipName)
  ) {
    reasons.push(
      `alias ${args.resolvedAlias} resolved to unexpected clip: ${args.resolvedClipName}`,
    );
  }
  const activeKey = args.activeAfter?.alias?.toLowerCase() ?? '';
  if (
    args.activeAfter !== null &&
    DEBUG_ONE_SHOT_ANIMATION_ALIASES.has(activeKey) &&
    !args.activeAfter.running
  ) {
    reasons.push(`one-shot appears active after finishing: ${args.activeAfter.alias}`);
  }
  return reasons;
}

function clipMatchesAlias(alias: string, clipName: string): boolean {
  const normalizedAlias = alias.toLowerCase();
  const normalizedClipName = clipName.toLowerCase();
  if (normalizedAlias === normalizedClipName) return true;
  const knownCandidates =
    ANIMATION_ALIASES[alias as CharacterAnimationAlias] ??
    ANIMATION_ALIASES[normalizedAlias as CharacterAnimationAlias];
  if (knownCandidates === undefined) return true;
  return knownCandidates.some((candidate) => candidate.toLowerCase() === normalizedClipName);
}

function configureAnimationAction(
  action: THREE.AnimationAction,
  aliasOrClipName: CharacterAnimationAlias | string,
  options: { readonly timeScale?: number } = {},
): void {
  const capability = getCharacterAnimationCapability(aliasOrClipName);
  const alias = (capability?.alias ?? aliasOrClipName).toLowerCase();
  const timeScale = options.timeScale ?? capability?.defaultTimeScale ?? 1;
  action.setEffectiveTimeScale(timeScale);
  if (ONE_SHOT_ANIMATION_ALIASES.has(alias)) {
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = false;
    return;
  }
  if (CLAMPED_ONE_SHOT_ANIMATION_ALIASES.has(alias)) {
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    return;
  }
  action.setLoop(THREE.LoopRepeat, Infinity);
  action.clampWhenFinished = false;
}

function sanitizeLocomotionClip(clip: THREE.AnimationClip): THREE.AnimationClip {
  const cloned = clip.clone();
  const freezeRootY = clipMatchesAlias('diceCatch', clip.name);
  cloned.tracks = cloned.tracks.map((track) => {
    if (!track.name.endsWith('.position')) return track;
    const targetName = track.name.slice(0, -'.position'.length);
    if (targetName !== 'Hips' && targetName !== 'Armature' && targetName !== 'Root') return track;
    if (!(track instanceof THREE.VectorKeyframeTrack)) return track;
    const values = track.values.slice();
    const rootY = values[1] ?? 0;
    for (let index = 0; index < values.length; index += 3) {
      values[index] = 0;
      if (freezeRootY && targetName === 'Hips') values[index + 1] = rootY;
      values[index + 2] = 0;
    }
    return new THREE.VectorKeyframeTrack(track.name, track.times.slice(), values);
  });
  return cloned;
}

function createStaticSockets(
  bodyMesh: THREE.Mesh,
  visualRoot: THREE.Group,
): Map<string, THREE.Object3D> {
  bodyMesh.geometry.computeBoundingBox();
  const box = bodyMesh.geometry.boundingBox;
  const maxY = box?.max.y ?? 1;
  const minY = box?.min.y ?? 0;
  const halfDepth = Math.max(Math.abs(box?.min.z ?? -0.35), Math.abs(box?.max.z ?? 0.35));
  const halfWidth = Math.max(Math.abs(box?.min.x ?? -0.35), Math.abs(box?.max.x ?? 0.35));
  const height = Math.max(maxY - minY, 1);
  const sockets = new Map<string, THREE.Object3D>();

  const add = (name: string, x: number, y: number, z: number): THREE.Group => {
    const socket = new THREE.Group();
    socket.name = `static-socket:${name}`;
    socket.position.set(x, y, z);
    visualRoot.add(socket);
    sockets.set(name, socket);
    return socket;
  };

  add('head', 0, maxY, 0);
  add('face', 0, maxY + height * 0.18, halfDepth + 0.08);
  add('cape', 0, maxY - height * 0.22, -halfDepth - 0.04);
  add('leftHand', -halfWidth - 0.12, minY + height * 0.58, 0.08);
  add('rightHand', halfWidth + 0.12, minY + height * 0.58, 0.08);
  return sockets;
}
